/**
 * Feedback de lanzamiento (temporal).
 *
 * Contrato backend (ya existe):
 *  - GET  /feedback/config          (público)      → { enabled }
 *  - POST /feedback                 (auth)         → 201 { ok:true }
 *  - GET  /admin/feedback?status=&type= (SUPER_ADMIN) → { items, new_count, enabled }
 *  - POST /admin/feedback/{id}/reviewed (SUPER_ADMIN) → { ok:true }  (alterna NEW<->REVIEWED)
 *
 * Toda la superficie usa el cliente `api` (mismo prefijo/base-path que el resto).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

export type FeedbackType = 'SUGGESTION' | 'BUG' | 'VERTICAL' | 'TEMPLATE' | 'OTHER';
export type FeedbackStatus = 'NEW' | 'REVIEWED';

/** Config pública: sólo dice si la recolección está encendida. */
export interface FeedbackConfig {
  enabled: boolean;
}

/** Payload de envío desde la web (`source` siempre 'WEB'). */
export interface SubmitFeedbackPayload {
  type: FeedbackType;
  message: string;
  /** Ruta desde donde se envió (ej. `window.location.pathname`). */
  context?: string;
  source: 'WEB';
}

/** Ítem tal como lo devuelve el panel Super Admin. */
export interface AdminFeedbackItem {
  id: string;
  type: FeedbackType;
  /** Etiqueta legible del tipo (localizada por el backend). */
  type_label: string;
  message: string;
  context: string | null;
  /** Origen del envío: 'WEB' | 'APP'. */
  source: string;
  status: FeedbackStatus;
  created_at: string;
  company_name: string | null;
  author: string | null;
  author_email: string | null;
}

export interface AdminFeedbackResponse {
  items: AdminFeedbackItem[];
  /** Cantidad total de ítems en estado NEW (para el badge del nav). */
  new_count: number;
  enabled: boolean;
}

/** Filtros del panel; cadena vacía = sin filtro. */
export interface AdminFeedbackFilters {
  status?: '' | FeedbackStatus;
  type?: '' | FeedbackType;
}

// --- Público / empresa ------------------------------------------------------

/** Config de feedback (público). Cacheado 60s: no cambia seguido. */
export function useFeedbackConfig() {
  return useQuery({
    queryKey: ['feedback', 'config'],
    queryFn: () => api.get<FeedbackConfig>('/feedback/config'),
    staleTime: 60_000,
  });
}

/** Envío de feedback (auth). El caller maneja los errores (429 / validación). */
export function useSubmitFeedback() {
  return useMutation({
    mutationFn: (payload: SubmitFeedbackPayload) =>
      api.post<{ ok: true }>('/feedback', payload),
  });
}

// --- Panel Super Admin ------------------------------------------------------

/** Lista de feedback filtrable por estado y tipo. */
export function useAdminFeedback(filters: AdminFeedbackFilters = {}) {
  const status = filters.status ?? '';
  const type = filters.type ?? '';
  return useQuery({
    queryKey: ['admin', 'feedback', status, type],
    queryFn: () => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (type) params.set('type', type);
      const qs = params.toString();
      return api.get<AdminFeedbackResponse>(`/admin/feedback${qs ? `?${qs}` : ''}`);
    },
  });
}

/** Alterna NEW<->REVIEWED e invalida la lista del panel. */
export function useToggleReviewed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ ok: true }>(`/admin/feedback/${id}/reviewed`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'feedback'] });
    },
  });
}
